import { useCallback, useMemo } from "react"

import { useMatch } from "react-router-dom"

import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { useIsFetchingNetwork, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useIsFetchingRoadway } from "../../../modules/roadway/hooks/useIsFetchingRoadway"
import { useIsFetchingRoadworks } from "../../../modules/roadworks/hooks/useIsFetchingRoadworks"
import { appRoad } from "../../constants/appRoad"

import { RoadMapLegend } from "../RoadMapLegend"

export function Map(props) {

    const {
        children,
        mapProps,
        ...otherProps
    } = props

    const isFetchingRoadway = useIsFetchingRoadway()
    const isFetchingRoadworks = useIsFetchingRoadworks()
    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetching = isFetchingRoadway || isFetchingRoadworks || isFetchingNetwork
    const { getSelectedNetworkBoundingBox } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { setMapBoundingBoxToFit } = mapDispatch
    const { error } = mapState
    const isMobile = useIsMobileDevice()

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = getSelectedNetworkBoundingBox()
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [getSelectedNetworkBoundingBox])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = getSelectedNetworkBoundingBox()
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [getSelectedNetworkBoundingBox, setMapBoundingBoxToFit])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined
    const match = useMatch("/:networkId/:modules/:subModule/*")
    const subModule = match?.params.subModule
    const module = Object.values(appRoad.modules).find(m => m.path === subModule)?.id

    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            initialViewState={mapInitialViewState}
            isBaseMap
            isLoading={isFetching}
            imageLayerDisplayed={imageLayerDisplayed}
            module={module}
            topLeftElements={!isMobile && [<RoadMapLegend key="road-map-legend" />]}
            topRightElements={isMobile && [<RoadMapLegend key="road-map-legend" />]}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            {...mapProps}
            {...otherProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}